import type { Locale } from "@gioia365/i18n";

// La finestra di Meta di «Registra su Meta e salva» (piano M6, R2, Task 7b; D1, D2 e A2a del controllo preliminare, questione 407):
// l'Embedded Signup di Meta in coesistenza con l'app WhatsApp Business del telefono, aperta con il JavaScript SDK di Facebook.
//
// - L'SDK si carica in anticipo, appena la finestra «Imposta» mostra il WhatsApp ufficiale (`preloadFacebookSdk`, decisione del
//   controller del 6 ottobre 2026): un `<script>` aggiunto alla pagina una volta sola (`https://connect.facebook.net/<lingua>/sdk.js`),
//   con `FB.init` dell'app di Meta e della versione della Graph API che il server dà in `metaSignup`. Così, con l'SDK pronto,
//   `openMetaSignupWindow` chiama `FB.login` subito, dentro il clic, e il browser non blocca la finestra di Meta (una finestra aperta
//   dopo un'attesa può essere bloccata). Il programma non ha una Content Security Policy (questione 407). Il caricamento sta dietro una
//   funzione sostituibile (`replaceFacebookSdkLoader`): nei test l'SDK è finto, nessuno scarica niente.
// - `FB.login` con la configurazione di Embedded Signup di Gioia365 (`config_id`), `response_type: "code"`,
//   `override_default_response_type: true` ed `extras` con `featureType: "whatsapp_business_app_onboarding"` e
//   `sessionInfoVersion: "3"` (le pagine di Meta della coesistenza): la risposta porta il codice da scambiare (vale 30 secondi).
// - La finestra di Meta dice com'è finita con un messaggio (`postMessage`) di tipo `WA_EMBEDDED_SIGNUP`: in coesistenza l'evento
//   `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` porta soltanto `data.waba_id` (il WhatsApp Business Account; il numero lo cerca il
//   server per E.164). Un messaggio vale soltanto se arriva da `facebook.com` o da un suo sottodominio, in https: ogni altra origine
//   si scarta, anche una che finisce per «facebook.com» senza il punto prima (`evilfacebook.com`).
// - L'SDK ha un limite di tempo (giro di correzioni del 7b): uno `<script>` che non arriva entro `SDK_LOAD_TIMEOUT_MS`, o che arriva
//   (`load`) ma non chiama `fbAsyncInit` entro `SDK_START_AFTER_LOAD_MS` (il sostituto che mettono alcuni blocchi della privacy, una
//   risposta che non è l'SDK), conta come non caricato: la finestra «Imposta» lo dice con «Apri la finestra di Meta». Gli eventi
//   automatici dell'app (`autoLogAppEvents`) sono spenti: l'Embedded Signup non ne ha bisogno.
// - L'attesa della finestra di Meta si può abbandonare (`signal`: «Annulla», la X o Esc della finestra «Imposta»): l'ascolto si
//   toglie e la risposta è `cancelled`; una risposta di Meta che arriva dopo si ignora.
// - Si risponde appena ci sono il codice e il WhatsApp Business Account, senza attese: il codice scade in 30 secondi. Se una delle
//   due metà non arriva entro `MISSING_HALF_MS` dall'altra, la finestra conta come non finita. Una finestra chiusa prima della fine
//   (o bloccata dal browser, che per l'SDK è la stessa cosa) è `cancelled`; l'evento `CANCEL` con `error_message` è `error`.

/** L'indirizzo dell'SDK di Facebook, nella lingua dell'interfaccia. */
export function facebookSdkUrl(locale: Locale): string {
  return `https://connect.facebook.net/${locale === "it" ? "it_IT" : "en_US"}/sdk.js`;
}

/** L'identificativo dello `<script>` dell'SDK, quello delle pagine di Meta: uno solo nella pagina. */
export const FACEBOOK_SDK_SCRIPT_ID = "facebook-jssdk";

/** Quanto si aspetta la seconda metà (il codice o il WhatsApp Business Account) dopo la prima. */
export const MISSING_HALF_MS = 10_000;

/** Quanto si aspetta l'SDK di Facebook, dall'aggiunta dello `<script>`. */
export const SDK_LOAD_TIMEOUT_MS = 15_000;

/** Quanto si aspetta `fbAsyncInit` dopo che lo `<script>` è arrivato (`load`). */
export const SDK_START_AFTER_LOAD_MS = 3_000;

/** I valori pubblici della finestra di Meta (`metaSignup` di `GET /api/settings/whatsapp-channels`) e la lingua dell'interfaccia. */
export type MetaSignupConfig = { appId: string; configId: string; graphVersion: string; locale: Locale };

/** La risposta di `FB.login`: con il codice quando la finestra è finita; senza `authResponse` se è stata chiusa o bloccata. */
export type FacebookLoginResponse = { authResponse?: { code?: string | null } | null; status?: string };

export type FacebookLoginOptions = {
  config_id: string;
  response_type: "code";
  override_default_response_type: true;
  extras: { setup: Record<string, never>; featureType: "whatsapp_business_app_onboarding"; sessionInfoVersion: "3" };
};

/** La parte dell'SDK di Facebook che Gioia365 usa. */
export type FacebookSdk = {
  init(options: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(callback: (response: FacebookLoginResponse) => void, options: FacebookLoginOptions): void;
};

/** Carica l'SDK e lo restituisce pronto (`FB.init` fatto). */
export type FacebookSdkLoader = (config: Omit<MetaSignupConfig, "configId">) => Promise<FacebookSdk>;

/** Le opzioni di `FB.login` per l'Embedded Signup in coesistenza. */
export function metaLoginOptions(configId: string): FacebookLoginOptions {
  return {
    config_id: configId,
    response_type: "code",
    override_default_response_type: true,
    extras: { setup: {}, featureType: "whatsapp_business_app_onboarding", sessionInfoVersion: "3" },
  };
}

type FacebookWindow = Window & { FB?: FacebookSdk; fbAsyncInit?: () => void };

// L'SDK caricato (o in caricamento), uno per pagina. Un caricamento non riuscito si dimentica: la volta dopo si riprova.
let loading: Promise<FacebookSdk> | null = null;

/**
 * Aggiunge lo `<script>` dell'SDK alla pagina (una volta) e lo inizializza con l'app di Meta: le istruzioni di Meta, con
 * `fbAsyncInit` che l'SDK chiama quando è pronto. Senza rete, con l'SDK bloccato dal browser, con uno script che non arriva in
 * tempo o che arriva e non parte, la promessa fallisce e lo `<script>` si toglie (la volta dopo se ne aggiunge uno nuovo).
 */
export const loadFacebookSdk: FacebookSdkLoader = ({ appId, graphVersion, locale }) => {
  if (loading) return loading;
  const target = window as FacebookWindow;
  const attempt = new Promise<FacebookSdk>((resolve, reject) => {
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    let settled = false;
    let script: HTMLScriptElement | null = null;
    const fail = (reason: string) => {
      if (settled) return;
      settled = true;
      timers.forEach(clearTimeout);
      if (target.fbAsyncInit === ready) delete target.fbAsyncInit;
      script?.remove();
      reject(new Error(reason));
    };
    function ready() {
      if (settled) return;
      const sdk = target.FB;
      if (!sdk) {
        fail("The Facebook SDK did not start");
        return;
      }
      settled = true;
      timers.forEach(clearTimeout);
      sdk.init({ appId, autoLogAppEvents: false, xfbml: false, version: graphVersion });
      resolve(sdk);
    }
    if (target.FB) {
      ready();
      return;
    }
    target.fbAsyncInit = ready;
    document.getElementById(FACEBOOK_SDK_SCRIPT_ID)?.remove();
    script = document.createElement("script");
    script.id = FACEBOOK_SDK_SCRIPT_ID;
    script.src = facebookSdkUrl(locale);
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.addEventListener("error", () => fail("The Facebook SDK could not be loaded"));
    script.addEventListener("load", () => timers.push(setTimeout(() => fail("The Facebook SDK arrived but did not start"), SDK_START_AFTER_LOAD_MS)));
    timers.push(setTimeout(() => fail("The Facebook SDK did not arrive in time"), SDK_LOAD_TIMEOUT_MS));
    document.head.appendChild(script);
  });
  loading = attempt;
  attempt.catch(() => {
    if (loading === attempt) loading = null;
  });
  return attempt;
};

let sdkLoader: FacebookSdkLoader = loadFacebookSdk;
// L'SDK chiesto in anticipo (o al clic) e, quando è arrivato, l'SDK pronto: con questo `FB.login` parte senza attese.
let preloaded: Promise<FacebookSdk> | null = null;
let readySdk: FacebookSdk | null = null;

/** Nei test: un SDK finto al posto di quello di Facebook (`null` rimette quello vero); in ogni caso si dimentica quello già caricato. */
export function replaceFacebookSdkLoader(loader: FacebookSdkLoader | null): void {
  sdkLoader = loader ?? loadFacebookSdk;
  preloaded = null;
  readySdk = null;
  if (loader === null) loading = null;
}

/**
 * Chiede l'SDK prima che serva (la finestra «Imposta» con il WhatsApp ufficiale): una volta sola; uno che non arriva si dimentica, e la
 * finestra di Meta lo richiede quando si apre.
 */
export function preloadFacebookSdk(config: Omit<MetaSignupConfig, "configId">): Promise<FacebookSdk> {
  if (preloaded) return preloaded;
  const attempt = sdkLoader(config);
  preloaded = attempt;
  attempt.then(
    (sdk) => {
      if (preloaded === attempt) readySdk = sdk;
    },
    () => {
      if (preloaded === attempt) preloaded = null;
    },
  );
  return attempt;
}

/**
 * L'SDK già pronto, se è arrivato: con questo una finestra di Facebook si apre subito, nello stesso clic. Lo usa anche «Collega la
 * pagina Facebook» degli ingressi dei Lead (`routes/settings/lead-intake-meta.ts`).
 */
export function readyFacebookSdk(): FacebookSdk | null {
  return readySdk;
}

/** Un'origine di Meta: `https://facebook.com` o un suo sottodominio (`https://www.facebook.com`, `https://business.facebook.com`). */
export function isFacebookOrigin(origin: string): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  return url.protocol === "https:" && (url.hostname === "facebook.com" || url.hostname.endsWith(".facebook.com"));
}

/** Che cosa dice un messaggio della finestra di Meta, se è suo e dell'Embedded Signup. */
export type MetaSignupEvent = { kind: "finished"; wabaId: string } | { kind: "cancelled" } | { kind: "error" };

const WABA_ID = /^[0-9]{1,32}$/;

/** Legge un messaggio della finestra di Meta; `null` per ogni altro messaggio (altre origini, altri tipi, testo non JSON). */
export function readMetaSignupEvent(event: Pick<MessageEvent, "origin" | "data">): MetaSignupEvent | null {
  if (!isFacebookOrigin(event.origin)) return null;
  let payload: unknown = event.data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (typeof payload !== "object" || payload === null) return null;
  const message = payload as { type?: unknown; event?: unknown; data?: unknown };
  if (message.type !== "WA_EMBEDDED_SIGNUP") return null;
  const data = typeof message.data === "object" && message.data !== null ? (message.data as Record<string, unknown>) : {};
  if (message.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
    const wabaId = data.waba_id;
    return typeof wabaId === "string" && WABA_ID.test(wabaId) ? { kind: "finished", wabaId } : { kind: "error" };
  }
  if (message.event === "CANCEL") return typeof data.error_message === "string" ? { kind: "error" } : { kind: "cancelled" };
  return null;
}

/**
 * Com'è finita la finestra di Meta: finita con il codice e il WhatsApp Business Account; chiusa prima della fine (o bloccata dal
 * browser); non finita per un errore di Meta, perché una delle due metà non è arrivata, o perché l'SDK non si è caricato.
 */
export type MetaSignupOutcome =
  | { kind: "finished"; code: string; wabaId: string }
  | { kind: "cancelled" }
  | { kind: "failed"; reason: "sdk_unavailable" | "incomplete" | "error" };

const CANCELLED: MetaSignupOutcome = { kind: "cancelled" };

/**
 * Apre la finestra di Meta e aspetta come finisce. Con l'SDK già pronto `FB.login` parte subito, nello stesso clic che l'ha chiesta;
 * altrimenti appena l'SDK arriva. Il codice va mandato al server subito: scade in 30 secondi. `signal` abbandona l'attesa (anche
 * quella dell'SDK): la risposta è `cancelled`, e `FB.login` non parte più se l'SDK arriva dopo.
 */
export function openMetaSignupWindow(config: MetaSignupConfig, signal?: AbortSignal): Promise<MetaSignupOutcome> {
  if (signal?.aborted) return Promise.resolve(CANCELLED);
  if (readySdk) return loginWith(readySdk, config.configId, signal);
  const loaded = preloadFacebookSdk({ appId: config.appId, graphVersion: config.graphVersion, locale: config.locale }).then(
    (sdk) => (signal?.aborted ? CANCELLED : loginWith(sdk, config.configId, signal)),
    (): MetaSignupOutcome => ({ kind: "failed", reason: "sdk_unavailable" }),
  );
  if (!signal) return loaded;
  return Promise.race([loaded, new Promise<MetaSignupOutcome>((resolve) => signal.addEventListener("abort", () => resolve(CANCELLED), { once: true }))]);
}

/** `FB.login` con l'ascolto dei messaggi della finestra di Meta: `FB.login` parte subito, dentro questa chiamata. */
function loginWith(sdk: FacebookSdk, configId: string, signal?: AbortSignal): Promise<MetaSignupOutcome> {
  return new Promise<MetaSignupOutcome>((resolve) => {
    let code: string | null = null;
    let wabaId: string | null = null;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Abbandonata: l'ascolto si toglie, e una risposta di Meta che arriva dopo non conta più.
    const abandon = () => settle(CANCELLED);
    const settle = (outcome: MetaSignupOutcome) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      signal?.removeEventListener("abort", abandon);
      if (timer !== null) clearTimeout(timer);
      resolve(outcome);
    };
    if (signal?.aborted) {
      settle(CANCELLED);
      return;
    }
    signal?.addEventListener("abort", abandon);
    // Appena ci sono le due metà si risponde; con una sola, si aspetta l'altra per un tempo breve.
    const progress = () => {
      if (code !== null && wabaId !== null) {
        settle({ kind: "finished", code, wabaId });
        return;
      }
      timer ??= setTimeout(() => settle({ kind: "failed", reason: "incomplete" }), MISSING_HALF_MS);
    };
    function onMessage(event: MessageEvent) {
      const read = readMetaSignupEvent(event);
      if (read === null) return;
      if (read.kind === "cancelled") settle({ kind: "cancelled" });
      else if (read.kind === "error") settle({ kind: "failed", reason: "error" });
      else {
        wabaId = read.wabaId;
        progress();
      }
    }

    window.addEventListener("message", onMessage);
    try {
      sdk.login((response) => {
        const received = response.authResponse?.code;
        if (typeof received === "string" && received !== "") {
          code = received;
          progress();
        } else if (wabaId === null) {
          settle({ kind: "cancelled" });
        } else {
          settle({ kind: "failed", reason: "incomplete" });
        }
      }, metaLoginOptions(configId));
    } catch {
      settle({ kind: "failed", reason: "sdk_unavailable" });
    }
  });
}
